// apps/web/src/features/kronos/timer/useTimer.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type TimerState,
  type AutoStopPreset,
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  computeElapsedMs,
  computeAutoStopAt,
  isStale,
  readTimer,
  writeTimer,
  clearTimer,
  STORAGE_KEY,
} from './timerMachine';

export interface TimerActions {
  start: (params: {
    projectId: string;
    designationId: string;
    autoStopPreset: AutoStopPreset;
    customAutoStopAt?: string;
    description?: string;
  }) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  discard: () => void;
  updateAutoStop: (preset: AutoStopPreset, customAt?: string) => void;
  updateProject: (projectId: string, designationId: string) => void;
  updateDescription: (description: string) => void;
}

export interface TimerHookReturn {
  /** Current timer state, or null if idle. */
  state: TimerState | null;
  /** Live elapsed milliseconds (updates every second when running). */
  elapsedMs: number;
  /** Whether a stale timer was found on mount and needs recovery. */
  needsRecovery: boolean;
  /** Timer actions. */
  actions: TimerActions;
  /** Dismiss the recovery state (after user saves or discards). */
  dismissRecovery: () => void;
}

export function useTimer(): TimerHookReturn {
  const [state, setState] = useState<TimerState | null>(() => readTimer());
  const [elapsedMs, setElapsedMs] = useState(0);
  const [needsRecovery, setNeedsRecovery] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // On mount: check for stale timer
  useEffect(() => {
    const saved = readTimer();
    if (saved && isStale(saved, Date.now())) {
      setNeedsRecovery(true);
      setState(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick the counter every second when running
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (state?.status === 'running' && !needsRecovery) {
      const tick = () => {
        const now = Date.now();
        setElapsedMs(computeElapsedMs(state, now));

        // Check auto-stop
        if (state.autoStopAt && now >= Date.parse(state.autoStopAt)) {
          const stopped = stopTimer(state, Date.parse(state.autoStopAt));
          writeTimer(stopped);
          setState(stopped);
        }
      };
      tick(); // immediate first tick
      intervalRef.current = setInterval(tick, 1000);
    } else if (state) {
      // Paused or stopped — compute once, don't tick
      setElapsedMs(computeElapsedMs(state, Date.now()));
    } else {
      setElapsedMs(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state, needsRecovery]);

  // Cross-tab sync via storage events
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const newState = readTimer();
      setState(newState);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const persist = useCallback((s: TimerState) => {
    writeTimer(s);
    setState(s);
  }, []);

  const actions: TimerActions = {
    start: useCallback(({ projectId, designationId, autoStopPreset, customAutoStopAt, description }) => {
      const now = Date.now();
      const autoStopAt = autoStopPreset === 'custom' && customAutoStopAt
        ? customAutoStopAt
        : computeAutoStopAt(autoStopPreset, now);
      const s = startTimer({ projectId, designationId, now, autoStopAt, description });
      persist(s);
      setNeedsRecovery(false);
    }, [persist]),

    pause: useCallback(() => {
      if (!state || state.status !== 'running') return;
      persist(pauseTimer(state, Date.now()));
    }, [state, persist]),

    resume: useCallback(() => {
      if (!state || state.status !== 'paused') return;
      persist(resumeTimer(state, Date.now()));
    }, [state, persist]),

    stop: useCallback(() => {
      if (!state || state.status === 'stopped') return;
      persist(stopTimer(state, Date.now()));
    }, [state, persist]),

    discard: useCallback(() => {
      clearTimer();
      setState(null);
      setElapsedMs(0);
      setNeedsRecovery(false);
    }, []),

    updateAutoStop: useCallback((preset, customAt) => {
      if (!state) return;
      const now = Date.now();
      const autoStopAt = preset === 'custom' && customAt
        ? customAt
        : computeAutoStopAt(preset, now);
      persist({ ...state, autoStopAt });
    }, [state, persist]),

    updateProject: useCallback((projectId, designationId) => {
      if (!state) return;
      persist({ ...state, projectId, designationId });
    }, [state, persist]),

    updateDescription: useCallback((description: string) => {
      if (!state) return;
      persist({ ...state, description });
    }, [state, persist]),
  };

  const dismissRecovery = useCallback(() => {
    setNeedsRecovery(false);
  }, []);

  return { state, elapsedMs, needsRecovery, actions, dismissRecovery };
}
